import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Plus, Filter, CalendarDays,
  CalendarCheck, ListTodo, Flag, MapPin, Trash2, Pencil, ClipboardList,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const WEEKDAYS_FULL = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const WEEKDAYS_MINI = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];
const MONTHS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

// Catégories alignées sur la maquette (5 types)
const CATEGORIES = {
  rdv:         { label: 'Rendez-vous client', dot: 'bg-blue-500',    border: 'border-l-blue-500',    bg: 'bg-blue-50',    text: 'text-blue-700' },
  installation:{ label: 'Installation',        dot: 'bg-emerald-500', border: 'border-l-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  maintenance: { label: 'Maintenance',         dot: 'bg-amber-500',   border: 'border-l-amber-500',   bg: 'bg-amber-50',   text: 'text-amber-700' },
  suivi:       { label: 'Suivi / Relance',     dot: 'bg-purple-500',  border: 'border-l-purple-500',  bg: 'bg-purple-50',  text: 'text-purple-700' },
  autre:       { label: 'Autre',               dot: 'bg-slate-400',   border: 'border-l-slate-400',   bg: 'bg-slate-50',   text: 'text-slate-700' },
};

// Jours fériés Centrafricains (date au format MM-DD ou exact YYYY-MM-DD)
const HOLIDAYS = [
  { date: '01-01', label: "Jour de l'An" },
  { date: '03-29', label: "Anniversaire de Boganda" },
  { date: '05-01', label: "Fête du Travail" },
  { date: '05-19', label: "Fête de l'Unité" },
  { date: '08-13', label: "Indépendance" },
  { date: '08-15', label: "Assomption" },
  { date: '11-01', label: "Toussaint" },
  { date: '12-01', label: "Fête nationale" },
  { date: '12-25', label: "Noël" },
];

const HOURS = Array.from({ length: 12 }, (_, i) => 8 + i); // 08:00 → 19:00

// --- Helpers de date -------------------------------------------------
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const startOfWeek = (d) => {
  const c = new Date(d);
  const day = c.getDay(); // 0=dim
  const diff = day === 0 ? -6 : 1 - day;
  c.setDate(c.getDate() + diff);
  c.setHours(0, 0, 0, 0);
  return c;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const sameDay = (a, b) => a && b && ymd(a) === ymd(b);
const fmtRange = (start) => {
  const end = addDays(start, 6);
  return `${start.getDate()} – ${end.getDate()} ${MONTHS_FR[end.getMonth()]} ${end.getFullYear()}`;
};
const fmtMonthYear = (d) => `${MONTHS_FR[d.getMonth()].charAt(0).toUpperCase() + MONTHS_FR[d.getMonth()].slice(1)} ${d.getFullYear()}`;
const isHoliday = (d) => {
  const key = `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return HOLIDAYS.find(h => h.date === key);
};

const TIME_TO_MIN = (t) => {
  const [h, m] = (t || '08:00').split(':').map(Number);
  return (isNaN(h) ? 8 : h) * 60 + (isNaN(m) ? 0 : m);
};

const guessCategory = (rdv) => {
  // RDV synchronisés depuis "Nos Panneaux" → catégorie 'maintenance'
  if (rdv.source === 'panneau' || rdv.type_contact === 'panneau') return 'maintenance';
  const r = `${rdv.raison || ''} ${rdv.notes || ''}`.toLowerCase();
  if (r.includes('installation') || r.includes('pose')) return 'installation';
  if (r.includes('maintenance') || r.includes('panneau') || r.includes('intervention') || r.includes('réparation')) return 'maintenance';
  if (r.includes('suivi') || r.includes('relance') || r.includes('appel')) return 'suivi';
  if (r.includes('rdv') || r.includes('rendez-vous') || r.includes('client') || r.includes('devis') || r.includes('négociation')) return 'rdv';
  return 'autre';
};

export default function Calendrier({ user, onLogout }) {
  const [today] = useState(new Date());
  const [cursor, setCursor] = useState(startOfWeek(new Date()));
  const [view, setView] = useState('semaine'); // jour | semaine | mois
  const [rdvs, setRdvs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date: ymd(new Date()),
    heure: '09:00',
    duree: '60',
    raison: '',
    nom_contact: '',
    entreprise: '',
    telephone: '',
    lieu: '',
    notes: '',
    type_contact: 'prospect',
    categorie: 'rdv',
  });
  const [miniCursor, setMiniCursor] = useState(new Date());
  const [filterCat, setFilterCat] = useState('all');
  // Nouveaux filtres façon maquette
  const [checkedCats, setCheckedCats] = useState(() => Object.keys(CATEGORIES).reduce((a, k) => ({ ...a, [k]: true }), {}));
  const [usersList, setUsersList] = useState([]);
  const [checkedUsers, setCheckedUsers] = useState({});

  // --- Loading ------------------------------------------------------
  const loadRdvs = async () => {
    try {
      const r = await axios.get(`${API}/api/rdv`);
      setRdvs(r.data || []);
    } catch {
      setRdvs([]);
    }
  };
  const loadUsers = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/users`);
      const list = (r.data || []).map(u => ({ id: u.email, name: u.name || u.email, email: u.email, role: u.role }));
      setUsersList(list);
      setCheckedUsers(list.reduce((a, u) => ({ ...a, [u.id]: true }), {}));
    } catch {
      setUsersList([]);
    }
  };
  useEffect(() => { loadRdvs(); loadUsers(); }, []);

  // --- CRUD --------------------------------------------------------
  const openCreate = (preset = {}) => {
    setEditing(null);
    setForm({
      date: preset.date || ymd(new Date()),
      heure: preset.heure || '09:00',
      duree: '60',
      raison: '',
      nom_contact: '',
      entreprise: '',
      telephone: '',
      lieu: '',
      notes: '',
      type_contact: 'prospect',
      categorie: preset.categorie || 'rdv',
    });
    setShowModal(true);
  };
  const openEdit = (rdv) => {
    setEditing(rdv);
    setForm({
      date: rdv.date,
      heure: rdv.heure,
      duree: rdv.duree || '60',
      raison: rdv.raison || '',
      nom_contact: rdv.nom_contact || '',
      entreprise: rdv.entreprise || '',
      telephone: rdv.telephone || '',
      lieu: rdv.lieu || '',
      notes: rdv.notes || '',
      type_contact: rdv.type_contact || 'prospect',
      categorie: rdv.categorie || guessCategory(rdv),
    });
    setShowModal(true);
  };
  const save = async () => {
    if (!form.raison || !form.date || !form.heure) {
      toast.error('Renseignez au moins la raison, la date et l\'heure');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/api/rdv/${editing.id}`, form);
        toast.success('Rendez-vous mis à jour');
      } else {
        await axios.post(`${API}/api/rdv`, form);
        toast.success('Rendez-vous créé');
      }
      setShowModal(false);
      loadRdvs();
    } catch {
      toast.error('Échec de la sauvegarde');
    }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return;
    try {
      await axios.delete(`${API}/api/rdv/${id}`);
      toast.success('Rendez-vous supprimé');
      setShowModal(false);
      loadRdvs();
    } catch {
      toast.error('Suppression impossible');
    }
  };

  // --- Données dérivées ----------------------------------------------
  const weekStart = useMemo(() => startOfWeek(cursor), [cursor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  // RDVs index par date YYYY-MM-DD
  const rdvByDate = useMemo(() => {
    const map = {};
    (rdvs || []).forEach(r => {
      if (!r.date) return;
      const cat = r.categorie || guessCategory(r);
      if (filterCat !== 'all' && cat !== filterCat) return;
      // Filtre par catégorie (checkboxes)
      if (checkedCats && checkedCats[cat] === false) return;
      // Filtre par utilisateur (created_by / user_email)
      const owner = r.created_by || r.user_email || r.email || '';
      if (owner && usersList.length > 0 && checkedUsers[owner] === false) return;
      (map[r.date] = map[r.date] || []).push(r);
    });
    return map;
  }, [rdvs, filterCat, checkedCats, checkedUsers, usersList]);

  // KPIs
  const todayStr = ymd(new Date());
  const weekStartStr = ymd(weekStart);
  const weekEndStr = ymd(addDays(weekStart, 6));
  const rdvToday = (rdvs || []).filter(r => r.date === todayStr).length;
  const rdvThisWeek = (rdvs || []).filter(r => r.date >= weekStartStr && r.date <= weekEndStr).length;
  const tasksLate = (rdvs || []).filter(r => r.date < todayStr && !r.fait).length;

  // Prochains rendez-vous
  const upcoming = (rdvs || [])
    .filter(r => r.date >= todayStr)
    .sort((a, b) => (a.date + a.heure).localeCompare(b.date + b.heure))
    .slice(0, 10);

  // --- Mini calendrier (panneau droit) ----------------------------
  const miniMonthDays = useMemo(() => {
    const start = startOfMonth(miniCursor);
    const startDay = (start.getDay() + 6) % 7; // Lun=0
    const monthStart = addDays(start, -startDay);
    return Array.from({ length: 42 }, (_, i) => addDays(monthStart, i));
  }, [miniCursor]);

  // --- Render ------------------------------------------------------
  const HolidayInfo = ({ d }) => {
    const h = isHoliday(d);
    return h ? (
      <div className="absolute inset-x-2 top-2 bottom-2 flex flex-col items-center justify-center text-red-500 z-0 select-none pointer-events-none">
        <Flag className="h-4 w-4 mb-1" />
        <span className="text-xs font-bold">Jour férié</span>
        <span className="text-[10px]">{h.label}</span>
      </div>
    ) : null;
  };

  const NonWorking = ({ d }) => {
    if (d.getDay() !== 0 && d.getDay() !== 6) return null;
    return (
      <div className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: 'repeating-linear-gradient(45deg, transparent 0 8px, rgba(148,163,184,0.06) 8px 12px)' }} />
    );
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="calendrier">
      <div className="space-y-4" data-testid="calendrier-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-[#1C2A46]">Calendrier</h1>
            <p className="text-sm text-slate-500">Gérez et programmez vos rendez-vous en toute simplicité</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white" data-testid="view-toggle">
              {[
                { id: 'jour', label: 'Jour' },
                { id: 'semaine', label: 'Semaine' },
                { id: 'mois', label: 'Mois' },
              ].map(v => (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  className={`px-4 py-2 text-sm font-medium transition first:rounded-l-lg last:rounded-r-lg ${
                    view === v.id ? 'bg-blue-600 text-white' : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  data-testid={`view-${v.id}`}
                >{v.label}</button>
              ))}
            </div>
            <Button variant="outline" onClick={() => setCursor(startOfWeek(new Date()))} data-testid="cal-today" className="h-9">
              Aujourd'hui
            </Button>
            <Button variant="outline" size="icon" onClick={() => setCursor(addDays(cursor, view === 'mois' ? -30 : view === 'jour' ? -1 : -7))} data-testid="cal-prev" className="h-9 w-9">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={() => setCursor(addDays(cursor, view === 'mois' ? 30 : view === 'jour' ? 1 : 7))} data-testid="cal-next" className="h-9 w-9">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button onClick={() => openCreate()} className="bg-blue-600 hover:bg-blue-700 gap-2 h-9" data-testid="new-rdv-btn">
              <Plus className="h-4 w-4" /> Nouveau RDV
            </Button>
          </div>
        </div>

        {/* Grid: Sidebar left + Calendar right */}
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          {/* ============ SIDEBAR GAUCHE ============ */}
          <aside className="space-y-4">
            {/* Mini calendar */}
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setMiniCursor(addDays(startOfMonth(miniCursor), -1))} className="p-1 hover:bg-slate-100 rounded">
                    <ChevronLeft className="h-4 w-4 text-slate-500" />
                  </button>
                  <p className="text-sm font-semibold text-[#1C2A46]">{fmtMonthYear(miniCursor)}</p>
                  <button onClick={() => setMiniCursor(addDays(startOfMonth(addDays(startOfMonth(miniCursor), 32)), 0))} className="p-1 hover:bg-slate-100 rounded">
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  </button>
                </div>
                <div className="grid grid-cols-7 text-center text-[10px] font-bold text-slate-400 mb-1">
                  {WEEKDAYS_MINI.map(w => <span key={w}>{w}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-y-1 text-center">
                  {miniMonthDays.map(d => {
                    const inMonth = d.getMonth() === miniCursor.getMonth();
                    const isToday = sameDay(d, new Date());
                    const isSelected = sameDay(d, cursor);
                    const hasHoliday = isHoliday(d);
                    return (
                      <button
                        key={ymd(d)}
                        onClick={() => setCursor(startOfWeek(d))}
                        className={`w-8 h-8 mx-auto rounded-full text-xs font-medium transition ${
                          isToday
                            ? 'bg-blue-600 text-white'
                            : isSelected
                              ? 'bg-blue-100 text-blue-700'
                              : !inMonth
                                ? 'text-slate-300'
                                : hasHoliday
                                  ? 'text-red-500'
                                  : 'text-[#1C2A46] hover:bg-slate-100'
                        }`}
                      >{d.getDate()}</button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Filtres */}
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm font-bold text-[#1C2A46] mb-3">Filtres</p>
                <Select value={filterCat} onValueChange={setFilterCat}>
                  <SelectTrigger className="h-9 mb-3" data-testid="cal-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les types</SelectItem>
                    {Object.entries(CATEGORIES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="space-y-2">
                  {Object.entries(CATEGORIES).map(([k, v]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checkedCats[k] !== false}
                        onChange={(e) => setCheckedCats({ ...checkedCats, [k]: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        data-testid={`cat-check-${k}`}
                      />
                      <span className={`w-2.5 h-2.5 rounded-full ${v.dot}`}></span>
                      <span className="text-slate-700">{v.label}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Utilisateurs */}
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <p className="text-sm font-bold text-[#1C2A46] mb-3">Utilisateurs</p>
                {usersList.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">Aucun utilisateur</p>
                ) : (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={usersList.every(u => checkedUsers[u.id] !== false)}
                        onChange={(e) => {
                          const val = e.target.checked;
                          setCheckedUsers(usersList.reduce((a, u) => ({ ...a, [u.id]: val }), {}));
                        }}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="font-semibold text-slate-700">Tous</span>
                    </label>
                    {usersList.map(u => (
                      <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checkedUsers[u.id] !== false}
                          onChange={(e) => setCheckedUsers({ ...checkedUsers, [u.id]: e.target.checked })}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`user-check-${u.id}`}
                        />
                        <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          {(u.name || u.email || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <span className="text-slate-700 truncate">{u.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* ============ CALENDRIER PRINCIPAL ============ */}
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-0 overflow-hidden">
              {/* Week header */}
              <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-200 bg-white">
                <div className="text-[10px] text-slate-400 font-bold text-center py-3 border-r border-slate-200">GMT+1</div>
                {weekDays.map((d) => {
                  const isToday = sameDay(d, new Date());
                  return (
                    <div key={ymd(d)} className="text-center py-3 border-r border-slate-200 last:border-r-0">
                      <p className="text-xs text-slate-500 font-semibold">
                        {WEEKDAYS_FULL[(d.getDay() + 6) % 7]}. {d.getDate()} {MONTHS_FR[d.getMonth()].slice(0, 3)}
                        {isToday && <span className="inline-flex ml-2 items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold align-middle">{d.getDate()}</span>}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Body : grille horaire */}
              <div className="overflow-y-auto max-h-[720px]">
                {HOURS.map((h) => (
                  <div key={h} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-slate-100 min-h-[70px]">
                    <div className="text-[11px] text-slate-400 font-medium text-right pr-2 pt-1 border-r border-slate-200">
                      {pad2(h)}:00
                    </div>
                    {weekDays.map(d => {
                      const events = (rdvByDate[ymd(d)] || []).filter(r => {
                        const mn = TIME_TO_MIN(r.heure);
                        return mn >= h * 60 && mn < (h + 1) * 60;
                      });
                      return (
                        <div
                          key={ymd(d) + h}
                          onClick={() => openCreate({ date: ymd(d), heure: `${pad2(h)}:00` })}
                          className="relative border-r border-slate-100 last:border-r-0 hover:bg-slate-50/50 cursor-pointer p-1"
                          data-testid={`cell-${ymd(d)}-${h}`}
                        >
                          <NonWorking d={d} />
                          {h === HOURS[0] && <HolidayInfo d={d} />}
                          {events.map(ev => {
                            const cat = ev.categorie || guessCategory(ev);
                            const c = CATEGORIES[cat] || CATEGORIES.rdv;
                            const endMn = TIME_TO_MIN(ev.heure) + (Number(ev.duree) || 60);
                            const endH = Math.floor(endMn / 60), endM = endMn % 60;
                            return (
                              <button
                                key={ev.id}
                                onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                                className={`relative z-20 block w-full text-left ${c.bg} border-l-4 ${c.border} rounded px-2 py-1.5 mb-1 hover:shadow-md hover:-translate-y-0.5 transition-all`}
                                data-testid={`evt-${ev.id}`}
                              >
                                <p className={`text-[10px] font-semibold ${c.text}`}>
                                  {ev.heure} – {pad2(endH)}:{pad2(endM)}
                                </p>
                                <p className="text-[11px] font-semibold text-slate-800 truncate leading-tight">
                                  {c.label.split(' /')[0]}
                                </p>
                                <p className="text-[11px] text-slate-700 truncate">{ev.raison || ev.entreprise || '—'}</p>
                                {ev.lieu && (
                                  <p className="text-[10px] text-slate-500 truncate flex items-center gap-0.5">
                                    <MapPin className="h-2.5 w-2.5" />{ev.lieu}
                                  </p>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div className="border-t border-slate-200 p-3 flex flex-wrap items-center justify-center gap-6 text-xs bg-white">
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 text-slate-600">
                    <span className={`w-2.5 h-2.5 rounded-full ${v.dot}`}></span>{v.label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal Nouveau / Edit RDV */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-600" />
              {editing ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
            </DialogTitle>
            <DialogDescription>
              Renseignez les informations du rendez-vous.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>Raison / Objet *</Label>
              <Input value={form.raison} onChange={(e) => setForm({ ...form, raison: e.target.value })} placeholder="Ex: Rendez-vous client SNE" data-testid="rdv-raison" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="rdv-date" />
              </div>
              <div>
                <Label>Heure *</Label>
                <Input type="time" value={form.heure} onChange={(e) => setForm({ ...form, heure: e.target.value })} data-testid="rdv-heure" />
              </div>
              <div>
                <Label>Durée (min)</Label>
                <Input type="number" value={form.duree} onChange={(e) => setForm({ ...form, duree: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => setForm({ ...form, categorie: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Contact</Label>
                <Input value={form.nom_contact} onChange={(e) => setForm({ ...form, nom_contact: e.target.value })} placeholder="Nom du contact" />
              </div>
              <div>
                <Label>Entreprise</Label>
                <Input value={form.entreprise} onChange={(e) => setForm({ ...form, entreprise: e.target.value })} placeholder="Ex: SNE" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Téléphone</Label>
                <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
              </div>
              <div>
                <Label>Lieu</Label>
                <Input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} placeholder="Adresse / Visio" />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {editing && (
              <Button variant="outline" onClick={() => remove(editing.id)} className="text-red-600 hover:text-red-700 mr-auto gap-1" data-testid="rdv-delete">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="rdv-save">
              {editing ? <><Pencil className="h-4 w-4" /> Enregistrer</> : <><Plus className="h-4 w-4" /> Créer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ---- Sous-composants ----
function KpiCard({ label, value, icon: Icon, color, danger }) {
  return (
    <div className="bg-white rounded-xl border-0 shadow-sm flex items-center gap-3 px-4 py-3">
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold truncate">{label}</p>
        <p className={`text-2xl font-bold leading-tight ${danger ? 'text-red-600' : 'text-[#1C2A46]'}`}>{value}</p>
      </div>
    </div>
  );
}
function SumRow({ label, value, danger }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-bold ${danger ? 'text-red-600' : 'text-[#1C2A46]'}`}>{value}</span>
    </div>
  );
}
